import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Heart, Plus, Minus } from "lucide-react";
import { Toaster, toast } from "sonner";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { formatApiError } from "@/contexts/AuthContext";
import BodyTypeSelector from "@/components/BodyTypeSelector";
import FindMySizeDrawer from "@/components/FindMySizeDrawer";

// Sizes considered pre-order (made-to-order). Everything else is in stock and
// eligible for expedite shipping.
const PREORDER_SIZES = ["XXL"];

// Delivery methods per shipping policy — see /app/frontend/src/pages/policies/Shipping.jsx
const IN_STOCK_METHODS = [
  {
    id: "standard",
    label: "Standard",
    detail: "7 business days · Complimentary",
    days: "7 business days",
  },
  {
    id: "expedite",
    label: "Expedite",
    detail: "3–4 business days · Complimentary",
    days: "3–4 business days",
  },
];

const PREORDER_METHOD = {
  id: "preorder",
  label: "Pre-Order (made-to-order)",
  detail: "14 business days · Complimentary",
  days: "14 business days",
};

export default function ProductPage() {
  const { handle } = useParams();
  const [products, setProducts] = useState([]);
  const [size, setSize] = useState("");
  const [bodyType, setBodyType] = useState(null);
  const [deliveryMethod, setDeliveryMethod] = useState("standard");
  const [findSizeOpen, setFindSizeOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const [saved, setSaved] = useState(false);
  const [openAccordion, setOpenAccordion] = useState(null); // controls dropdown accordions
  const { addToCart } = useCart();
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const product = products.find((p) => p.handle === handle);

  const gallery = useMemo(() => {
    if (!product) return [];
    return product.gallery?.length ? product.gallery : [product.image];
  }, [product]);

  const isPreorderSize = size ? PREORDER_SIZES.includes(size) : false;

  // If user picks a pre-order size after having selected expedite, reset method.
  useEffect(() => {
    if (isPreorderSize) setDeliveryMethod("preorder");
    else if (deliveryMethod === "preorder") setDeliveryMethod("standard");
  }, [isPreorderSize]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!product) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-32 text-[#000000]/70">
        Loading…
      </div>
    );
  }

  const variant =
    product.variants.find((v) => v.size === size) || product.variants[0];

  const discountPct = product.compare_at_price
    ? Math.round(
        ((product.compare_at_price - product.price) / product.compare_at_price) * 100,
      )
    : 0;

  const handleAdd = async () => {
    if (!size) {
      toast.error("Please select a size");
      return;
    }
    setAdding(true);
    try {
      await addToCart({
        product_id: product.product_id,
        variant_id: variant.variant_id,
        size: variant.size,
        quantity: 1,
        body_type: bodyType,
      });
      toast.success(`${product.title} added to bag`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAdding(false);
    }
  };

  const toggleSaved = () => {
    setSaved((v) => !v);
    toast.success(saved ? "Removed from saved items" : "Saved to your wishlist");
  };

  return (
    <div data-testid="product-page" className="bg-[#ffffff] min-h-screen">
      {/* Natural-material disclaimer strip — animated marquee, sticky so it
          stays visible while scrolling the whole listing */}
      {product.texture_note && (
        <div
          data-testid="product-texture-note"
          className="sticky top-20 z-40 w-full bg-[#f5c518] text-black border-y border-black/20 overflow-hidden shadow-sm"
        >
          <div className="product-texture-marquee py-2.5 whitespace-nowrap">
            {[0, 1, 2, 3].map((k) => (
              <span
                key={k}
                className="inline-block px-8 text-[11px] md:text-[12px] tracking-[0.28em] uppercase text-black font-bold"
              >
                <span className="mr-3">◆</span>
                {product.texture_note}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-6 pb-4">
        <p className="text-[10px] tracking-[0.22em] uppercase text-[#000000]/40">
          <Link to="/" className="hover:text-[#c9a84c]">Home</Link>
          {" / "}
          <Link to="/shop" className="hover:text-[#c9a84c]">Leather Jackets</Link>
          {" / "}
          <span className="text-[#000000]/70">{product.title}</span>
        </p>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 lg:gap-16 pb-24">
        {/* LEFT — vertically stacked gallery */}
        <section
          className="lg:col-span-8 space-y-3"
          data-testid="product-gallery"
        >
          {gallery.map((src, i) => (
            <figure
              key={i}
              className="relative bg-[#f5f5f5] overflow-hidden aspect-[4/5]"
              data-testid={`product-image-${i}`}
            >
              <img
                src={src}
                alt={`${product.title} – image ${i + 1} of ${gallery.length}`}
                className="absolute inset-0 w-full h-full object-cover"
                loading={i === 0 ? "eager" : "lazy"}
              />
              <figcaption
                className="absolute bottom-4 left-4 text-[10px] tracking-[0.28em] uppercase text-white bg-black/40 backdrop-blur-sm px-3 py-1"
                aria-hidden="true"
              >
                Image {i + 1} of {gallery.length}
              </figcaption>
            </figure>
          ))}
        </section>

        {/* RIGHT — sticky info panel */}
        <aside
          className="lg:col-span-4 lg:sticky lg:top-28 self-start"
          data-testid="product-info-panel"
        >
          <Link
            to="/shop"
            data-testid="product-brand-link"
            className="text-[11px] tracking-[0.28em] uppercase text-[#000000]/85 hover:text-[#c9a84c] inline-block"
          >
            BOSCAGE
          </Link>

          <h1
            data-testid="product-title"
            className="font-serif text-3xl md:text-4xl leading-tight mt-3"
          >
            {product.title}
          </h1>
          <p
            data-testid="product-subtitle"
            className="text-[#000000]/55 mt-1 text-sm"
          >
            {product.subtitle}
          </p>

          {/* Price */}
          <div className="mt-6" data-testid="product-price-block">
            <div className="flex items-baseline gap-3">
              {product.compare_at_price && (
                <span className="text-base text-[#000000]/45 line-through">
                  ${product.compare_at_price.toFixed(0)}
                </span>
              )}
              <span
                data-testid="product-price"
                className="font-serif text-2xl text-[#000000]"
              >
                ${product.price.toFixed(0)}
              </span>
              {discountPct > 0 && (
                <span className="text-xs tracking-[0.18em] uppercase text-[#c9a84c]">
                  −{discountPct}%
                </span>
              )}
            </div>
          </div>

          {/* Short description — always visible */}
          <p
            data-testid="product-description"
            className="mt-6 text-sm leading-relaxed text-[#000000]/80"
          >
            {product.description}
          </p>

          {/* Size + guide */}
          <div className="mt-8 flex items-center justify-between">
            <span className="text-[11px] tracking-[0.22em] uppercase text-[#000000]/70">
              Size
            </span>
            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => setFindSizeOpen(true)}
                data-testid="find-my-size-link"
                className="text-[11px] tracking-[0.18em] uppercase text-[#c9a84c] hover:underline"
              >
                Find my size
              </button>
              <Link
                to="/size-guide"
                data-testid="size-guide-link"
                className="text-[11px] tracking-[0.18em] uppercase text-[#c9a84c] hover:underline"
              >
                Size guide
              </Link>
            </div>
          </div>

          <div
            className="grid grid-cols-5 gap-2 mt-3"
            data-testid="size-selector"
          >
            {product.sizes.map((s) => (
              <button
                key={s}
                data-testid={`size-option-${s.toLowerCase()}`}
                onClick={() => setSize(s)}
                className={`py-3 text-sm tracking-wider border transition-colors ${
                  size === s
                    ? "border-[#c9a84c] text-[#c9a84c]"
                    : "border-[#000000]/20 text-[#000000]/75 hover:border-[#000000]/60"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          {!size && (
            <p
              data-testid="size-hint"
              className="text-[10px] tracking-[0.22em] uppercase text-[#000000]/40 mt-2"
            >
              Select a size
            </p>
          )}

          {/* Body Type — UI-only fit note, sent to cart as a line property */}
          <BodyTypeSelector value={bodyType} onChange={setBodyType} />

          {/* Delivery method (only after size is selected) */}
          {size && (
            <div
              className="mt-6 border border-[#000000]/15 p-4 space-y-3"
              data-testid="delivery-method-block"
            >
              <p className="text-[11px] tracking-[0.22em] uppercase text-[#000000]/70">
                Delivery method
              </p>
              {isPreorderSize ? (
                <label
                  className="flex items-start gap-3 cursor-default"
                  data-testid="delivery-option-preorder"
                >
                  <input
                    type="radio"
                    checked
                    readOnly
                    className="mt-1 accent-[#c9a84c]"
                  />
                  <span>
                    <span className="block text-sm text-[#000000]">
                      {PREORDER_METHOD.label}
                    </span>
                    <span className="block text-[11px] tracking-[0.18em] uppercase text-[#000000]/55 mt-1">
                      {PREORDER_METHOD.detail}
                    </span>
                    <span className="block text-[10px] tracking-[0.22em] uppercase text-[#c9a84c] mt-2">
                      Made-to-order · size {size}
                    </span>
                  </span>
                </label>
              ) : (
                IN_STOCK_METHODS.map((m) => (
                  <label
                    key={m.id}
                    data-testid={`delivery-option-${m.id}`}
                    className="flex items-start gap-3 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="delivery-method"
                      value={m.id}
                      checked={deliveryMethod === m.id}
                      onChange={() => setDeliveryMethod(m.id)}
                      className="mt-1 accent-[#c9a84c]"
                    />
                    <span>
                      <span className="block text-sm text-[#000000]">
                        {m.label}
                      </span>
                      <span className="block text-[11px] tracking-[0.18em] uppercase text-[#000000]/55 mt-1">
                        {m.detail}
                      </span>
                    </span>
                  </label>
                ))
              )}
              <p className="text-[10px] tracking-[0.22em] uppercase text-[#000000]/40 pt-2 border-t border-[#000000]/10">
                Complimentary shipping — USA, Canada, Mexico only
              </p>
            </div>
          )}

          {/* Add to bag */}
          <div className="mt-6 space-y-3">
            <button
              data-testid="add-to-cart-button"
              onClick={handleAdd}
              disabled={adding}
              className="btn-primary w-full"
            >
              {adding
                ? "Adding…"
                : isPreorderSize
                ? "Reserve · pre-order"
                : "Add to bag"}
            </button>
            <button
              data-testid="save-button"
              onClick={toggleSaved}
              className="btn-secondary-dark w-full inline-flex items-center justify-center gap-2"
              aria-pressed={saved}
            >
              <Heart
                size={14}
                strokeWidth={1.5}
                className={saved ? "fill-[#c9a84c] text-[#c9a84c]" : ""}
              />
              {saved ? "Saved" : "Save"}
            </button>
          </div>

          {/* Accordions */}
          <div
            className="mt-10 border-t border-[#000000]/10"
            data-testid="product-accordions"
          >
            <Accordion
              testId="acc-features"
              title="Key features"
              open={openAccordion === "features"}
              onToggle={() =>
                setOpenAccordion((v) => (v === "features" ? null : "features"))
              }
            >
              <ul className="space-y-1.5 text-sm text-[#000000]/80">
                {(product.features?.length
                  ? product.features
                  : product.highlights
                ).map((h, i) => (
                  <li key={i} className="flex gap-2">
                    <span className="text-[#000000]/40">·</span>
                    {h}
                  </li>
                ))}
              </ul>
            </Accordion>

            <Accordion
              testId="acc-composition"
              title="Composition & origin"
              open={openAccordion === "composition"}
              onToggle={() =>
                setOpenAccordion((v) =>
                  v === "composition" ? null : "composition",
                )
              }
            >
              <p className="text-sm text-[#000000]/80">{product.composition}</p>
              <dl className="mt-4 space-y-3 text-[11px] tracking-[0.22em] uppercase text-[#000000]/65">
                {product.leather_origin && (
                  <div className="flex justify-between border-b border-[#000000]/10 pb-2">
                    <dt>Leather origin</dt>
                    <dd className="text-[#000000]">{product.leather_origin}</dd>
                  </div>
                )}
                {product.leather_thickness && (
                  <div className="flex justify-between border-b border-[#000000]/10 pb-2">
                    <dt>Thickness</dt>
                    <dd className="text-[#000000]">
                      {product.leather_thickness}
                    </dd>
                  </div>
                )}
                {product.fit_note && (
                  <div className="flex justify-between border-b border-[#000000]/10 pb-2">
                    <dt>Fit</dt>
                    <dd className="text-[#000000] text-right max-w-[60%] normal-case tracking-normal">
                      {product.fit_note}
                    </dd>
                  </div>
                )}
              </dl>
            </Accordion>

            <Accordion
              testId="acc-care"
              title="Product care"
              open={openAccordion === "care"}
              onToggle={() =>
                setOpenAccordion((v) => (v === "care" ? null : "care"))
              }
            >
              {product.care?.do?.length || product.care?.dont?.length ? (
                <div className="grid sm:grid-cols-2 gap-6">
                  <div data-testid="product-care-do">
                    <p className="text-[11px] tracking-[0.28em] uppercase text-[#000000]/70 mb-2">
                      Do
                    </p>
                    <ul className="space-y-1.5 text-sm text-[#000000]/80">
                      {product.care.do.map((d, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="text-[#c9a84c]">+</span>
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div data-testid="product-care-dont">
                    <p className="text-[11px] tracking-[0.28em] uppercase text-[#000000]/70 mb-2">
                      Don&apos;t
                    </p>
                    <ul className="space-y-1.5 text-sm text-[#000000]/80">
                      {product.care.dont.map((d, i) => (
                        <li key={i} className="flex gap-2">
                          <span className="text-[#000000]/40">−</span>
                          {d}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-[#000000]/80">{product.washing}</p>
              )}
            </Accordion>

            <Accordion
              testId="acc-wearing"
              title="Wearing"
              open={openAccordion === "wearing"}
              onToggle={() =>
                setOpenAccordion((v) => (v === "wearing" ? null : "wearing"))
              }
            >
              <p className="text-sm text-[#000000]/80">{product.wearing}</p>
              <p className="text-[10px] tracking-[0.18em] uppercase text-[#000000]/40 mt-4">
                BOSCAGE ID: {product.product_id.toUpperCase()}
              </p>
            </Accordion>

            <Accordion
              testId="acc-delivery"
              title="Delivery & returns"
              open={openAccordion === "delivery"}
              onToggle={() =>
                setOpenAccordion((v) => (v === "delivery" ? null : "delivery"))
              }
            >
              <ul className="space-y-3 text-sm text-[#000000]/80">
                <li>
                  <span className="block text-[11px] tracking-[0.22em] uppercase text-[#c9a84c] mb-1">
                    Standard shipping
                  </span>
                  7 business days · Complimentary. Sizes S–XL ship from stock.
                </li>
                <li>
                  <span className="block text-[11px] tracking-[0.22em] uppercase text-[#c9a84c] mb-1">
                    Expedite shipping
                  </span>
                  3–4 business days · Complimentary. Available for in-stock sizes (S–XL).
                </li>
                <li>
                  <span className="block text-[11px] tracking-[0.22em] uppercase text-[#c9a84c] mb-1">
                    Pre-order (XXL)
                  </span>
                  Made-to-order — 14 business days. Complimentary shipping.
                </li>
                <li>
                  <span className="block text-[11px] tracking-[0.22em] uppercase text-[#c9a84c] mb-1">
                    14-day returns
                  </span>
                  Return any unworn piece within 14 days for a full refund.
                </li>
                <li>
                  <span className="block text-[11px] tracking-[0.22em] uppercase text-[#c9a84c] mb-1">
                    Lifetime guarantee
                  </span>
                  Numbered &amp; registered to you. Restoration through the BOSCAGE Renewal Program.
                </li>
              </ul>
              <p className="mt-5 text-[11px] tracking-[0.18em] uppercase text-[#000000]/45">
                Full details on our{" "}
                <Link to="/shipping" className="text-[#c9a84c] hover:underline">
                  Shipping
                </Link>{" "}
                and{" "}
                <Link to="/returns" className="text-[#c9a84c] hover:underline">
                  Returns
                </Link>{" "}
                pages.
              </p>
            </Accordion>
          </div>

          {/* Personalize CTA */}
          <button
            data-testid="personalize-jacket-button"
            onClick={() => navigate("/personalize")}
            className="btn-secondary-dark w-full mt-10"
          >
            Personalize this jacket
          </button>
        </aside>
      </div>

      {/* ─────────── Lookbook / Get The Look — LIGHT theme, uncropped ─────────── */}
      {product.lookbook?.length > 0 && (
        <section
          data-testid="product-lookbook"
          className="bg-[#ffffff] text-[#000000] py-20 md:py-28 border-t border-[#000000]/10"
        >
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="mb-12 md:mb-16 max-w-2xl">
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#c9a84c]">
                Get the look
              </p>
              <h2 className="font-serif text-3xl md:text-4xl mt-4 leading-tight text-[#000000]">
                Studied. Styled. Photographed.
              </h2>
              <p className="mt-4 text-sm md:text-base text-[#000000]/65 leading-relaxed">
                A closer look at the Refined Biker in the wild — full-frame
                photography, presented without crop so every stitch reads.
              </p>
            </div>

            <div className="space-y-10 md:space-y-14">
              {product.lookbook.map((src, i) => (
                <figure
                  key={i}
                  data-testid={`product-lookbook-${i}`}
                  className="relative w-full bg-[#f5f5f5]"
                >
                  <img
                    src={src}
                    alt={`${product.title} editorial ${i + 1}`}
                    loading="lazy"
                    className="w-full h-auto block object-contain"
                  />
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      <Toaster theme="dark" position="top-center" />

      <FindMySizeDrawer
        open={findSizeOpen}
        onClose={() => setFindSizeOpen(false)}
        availableSizes={product.sizes}
        onPick={(recommended) => {
          setSize(recommended);
          toast.success(`Size ${recommended} selected — scroll to Add to Bag`);
        }}
      />
    </div>
  );
}

/* ─────────── Accordion ─────────── */
const Accordion = ({ testId, title, open, onToggle, children }) => (
  <div
    data-testid={testId}
    className="border-b border-[#000000]/10"
  >
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      data-testid={`${testId}-toggle`}
      className="w-full flex items-center justify-between py-5 text-left text-[13px] tracking-[0.18em] uppercase text-[#000000] hover:text-[#c9a84c] transition-colors"
    >
      <span>{title}</span>
      {open ? (
        <Minus size={16} strokeWidth={1.5} className="text-[#000000]/70" />
      ) : (
        <Plus size={16} strokeWidth={1.5} className="text-[#000000]/70" />
      )}
    </button>
    <div
      className={`overflow-hidden transition-all duration-300 ease-out ${
        open ? "max-h-[1500px] opacity-100 pb-6" : "max-h-0 opacity-0"
      }`}
      data-testid={`${testId}-panel`}
    >
      {children}
    </div>
  </div>
);
